import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { auditTime, fromEvent, map, Observable, of } from 'rxjs';
import { WINDOW, MediaScreenService, CommonMediaQueries } from '@hglobal/common';
import { THEME_STORAGE_KEY, UI_IS_DARK_THEME_KEY } from '@pt/constants';

@Injectable({ providedIn: 'root' })
export class UiStore {
  readonly #window = inject(WINDOW);
  readonly #document = inject(DOCUMENT);
  readonly #mediaScreenService = inject(MediaScreenService);

  readonly headerHeight = signal(0);

  readonly isDesktop = toSignal(this.#mediaScreenService.mediaMatcher(CommonMediaQueries.LG));
  readonly isTabletAndMore = toSignal(this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD));
  readonly isTablet = computed(() => this.isTabletAndMore() && !this.isDesktop());
  readonly isPhoneAndMore = toSignal(this.#mediaScreenService.mediaMatcher(CommonMediaQueries.XS));
  readonly isPhone = computed(() => !this.isTabletAndMore());

  readonly isDarkTheme = signal(false);
  readonly #isDarkThemeEffect = effect(() => {
    this.#window.localStorage.setItem(UI_IS_DARK_THEME_KEY, this.isDarkTheme() ? 'true' : 'false');
    this.#window.localStorage.setItem(THEME_STORAGE_KEY, this.isDarkTheme() ? 'dark' : 'light');

    if (this.isDarkTheme()) {
      this.#document.documentElement.classList.add('pu-dark-theme');
    } else {
      this.#document.documentElement.classList.remove('pu-dark-theme');
    }
  });

  readonly isNavbarHidden = signal(true);
  readonly isHeaderShadowVisible = signal(false);
  #currentScrollTop = 0;
  readonly #documentScrollEvent = fromEvent(this.#document, 'scroll')
    .pipe(
      auditTime(200),
      map(event => {
        const threshold = this.#mediaScreenService.isMatched(CommonMediaQueries.LG) ? 0 : 70;
        const target = event.target as Document;
        const scrollTop = target.scrollingElement.scrollTop;
        const isScrollBottom = target.scrollingElement.clientHeight + scrollTop
          >= target.scrollingElement.scrollHeight;
        this.isNavbarHidden.set(scrollTop > threshold
          && scrollTop > this.#currentScrollTop && !isScrollBottom);
        this.isHeaderShadowVisible.set(scrollTop > threshold);
        this.#currentScrollTop = scrollTop;
      }),
      takeUntilDestroyed(),
    )
    .subscribe();

  init(): Observable<void> {
    this.isDarkTheme.set(this.#window.localStorage.getItem(UI_IS_DARK_THEME_KEY) === 'true');
    return of();
  }
}
